import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { extractReadableErrorFrom } from "../../common/helpers/extractReadableError";
import { usageWindow } from "../../common/helpers/eplex/billingUsage";

import {
  accrueTokens,
  applyFreeTariff,
  disableBillingProfileAccount,
  fetchBillingBalance,
  fetchBillingClients,
  fetchBillingHistory,
  fetchBillingPricingRules,
  fetchBillingProfileDetail,
  fetchBillingProfiles,
  fetchBillingTariffs,
  fetchBillingTariffVersions,
  fetchBillingUsageDaily,
  fetchTokenPrice,
  payTariff,
  updateBillingProfileAccount,
} from "../../api/billingApi";
import type {
  BillingAccountUpdatePayload,
  BillingBalance,
  BillingClient,
  BillingHistoryFilters,
  BillingHistoryItem,
  BillingPriceByMonths,
  BillingPricingRule,
  BillingProfileDetail,
  BillingProfileSummary,
  BillingRole,
  BillingTariff,
  BillingTariffPayPayload,
  BillingTokenPrice,
  BillingUsageDailyItem,
  TariffVersion,
} from "../../models/billing/Billing";

/** Стабильный ключ-сентинел ошибки: пакет уже стоит в очереди (deferred apply). */
export const QUEUED_PACKAGE_EXISTS = "queued_package_exists";

/** Окно спарклайна «Расход за 14 дней» на карточке баланса — число зашито и в подписи `billing.hero.sparklineTitle`. */
export const USAGE_WINDOW_DAYS = 14;

const SELECTED_CLIENT_KEY = "billing.lastClientId";
const HISTORY_PAGE_SIZE = 50;

type Status = "idle" | "loading" | "ready" | "error";

function readSelectedClientId(): number | null {
  try {
    const raw = localStorage.getItem(SELECTED_CLIENT_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  } catch (error) {
    console.error("billing: cannot read selected client id", error);
  }
  return null;
}

function writeSelectedClientId(id: number | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(SELECTED_CLIENT_KEY);
    } else {
      localStorage.setItem(SELECTED_CLIENT_KEY, String(id));
    }
  } catch (error) {
    console.error("billing: cannot persist selected client id", error);
  }
}

function extractErrorMessage(error: unknown): string {
  // Биллинг-прокси заворачивает детальный текст в `data.error.message` вида
  // «Неопознанная ошибка Data: {...,"err":"..."}» — раскодируем вложенный `err`,
  // иначе в карточке ошибки виден сырой JSON с `\uXXXX`-escape.
  return extractReadableErrorFrom(error) ?? "Ошибка запроса";
}

/**
 * Отображает ошибку покупки в стабильный ключ-сентинел. Пока распознаём только
 * `queued_package_exists` (у клиента уже есть отложенный пакет; биллинг отдаёт
 * HTTP 422 с машиночитаемым `error.code`). Матчим прежде всего по `error.code`,
 * с подстраховкой по сообщению. Стор i18n-free — страница сама переводит ключ.
 * `null` — ошибка нераспознана.
 */
function mapPurchaseError(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;
  const maybeAxios = error as {
    response?: { data?: { error?: { code?: string; message?: string } } };
    message?: string;
  };
  const code = maybeAxios.response?.data?.error?.code;
  const message = maybeAxios.response?.data?.error?.message ?? maybeAxios.message ?? "";
  if (code === QUEUED_PACKAGE_EXISTS || /queued[_ ]?package[_ ]?exists/i.test(message)) {
    return QUEUED_PACKAGE_EXISTS;
  }
  return null;
}

export const useBillingStore = defineStore("eplexBilling", () => {
  // clients
  const clients = ref<BillingClient[]>([]);
  const clientsStatus = ref<Status>("idle");
  const clientsError = ref<string | null>(null);

  // selection
  const selectedClientId = ref<number | null>(readSelectedClientId());

  // balance
  const balance = ref<BillingBalance | null>(null);
  const balanceStatus = ref<Status>("idle");
  const balanceError = ref<string | null>(null);

  // profiles
  const profiles = ref<BillingProfileSummary[]>([]);
  const profilesStatus = ref<Status>("idle");
  const profilesError = ref<string | null>(null);

  // selected profile detail
  const profileDetail = ref<BillingProfileDetail | null>(null);
  const profileDetailStatus = ref<Status>("idle");
  const profileDetailError = ref<string | null>(null);

  // history
  const history = ref<BillingHistoryItem[]>([]);
  const historyStatus = ref<Status>("idle");
  const historyError = ref<string | null>(null);
  const historyCursor = ref<string | null>(null);
  const historyHasMore = ref(false);
  const historyFilters = ref<Omit<BillingHistoryFilters, "client_id" | "cursor" | "limit">>({});

  // суточный расход для спарклайна — готовый ряд с бэка, не агрегат по странице истории (CAB-725)
  const usageDaily = ref<BillingUsageDailyItem[]>([]);
  const usageDailyStatus = ref<Status>("idle");

  // tariffs catalog + pricing rules (for cards and the calculator)
  const tariffs = ref<BillingTariff[]>([]);
  const tariffsStatus = ref<Status>("idle");
  const tariffsError = ref<string | null>(null);

  const pricingRules = ref<BillingPricingRule[]>([]);
  const pricingRulesStatus = ref<Status>("idle");
  const pricingRulesError = ref<string | null>(null);

  // Прайсинг по длительности с активной версии тарифа, кэш по коду тарифа.
  const tariffPricing = ref<Record<string, BillingPriceByMonths>>({});

  // фиксированная цена докупки токена (LK-слой)
  const tokenPrice = ref<BillingTokenPrice | null>(null);

  // purchase (buy tariff / buy tokens) — open the returned payment link
  const purchaseStatus = ref<Status>("idle");
  // распознанный ключ-сентинел ошибки покупки (переводит страница), либо null
  const purchaseError = ref<string | null>(null);

  const currentTariffCode = computed<string | null>(() => balance.value?.tariff_code ?? null);

  const selectedClient = computed<BillingClient | null>(() => {
    const id = selectedClientId.value;
    if (id === null) return null;
    return clients.value.find((c) => c.external_client_id === id) ?? null;
  });

  const myRoles = computed<BillingRole[]>(() => {
    // We don't have an explicit "current profile" endpoint — we use the profiles list of
    // the selected client and try to match by current user's profile id stored in localStorage.
    // As a fallback we infer admin from `relationship !== 'own'` or presence of profiles list.
    // This is best-effort until backend exposes /me; the UI gates are conservative.
    const myProfileId = readCurrentProfileId();
    if (myProfileId !== null) {
      const me = profiles.value.find((p) => p.profile_id === myProfileId);
      if (me?.account?.roles?.length) return me.account.roles;
    }
    return [];
  });

  const isAdminLikeRole = computed<boolean>(() => {
    if (myRoles.value.some((r) => r === "client_admin" || r === "eplex_partner" || r === "eplex_accountant")) {
      return true;
    }
    // If the user can list more than one client (or partner clients) — they are at least a partner-level admin.
    if (clients.value.some((c) => c.relationship !== "own")) return true;
    return false;
  });

  function readCurrentProfileId(): number | null {
    try {
      const raw = localStorage.getItem("_user");
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { profileId?: number };
      const id = parsed?.profileId;
      return typeof id === "number" && id > 0 ? id : null;
    } catch (error) {
      console.error("billing: cannot read _user", error);
      return null;
    }
  }

  function setSelectedClient(id: number | null): void {
    selectedClientId.value = id;
    writeSelectedClientId(id);
    balance.value = null;
    profiles.value = [];
    history.value = [];
    historyCursor.value = null;
    historyHasMore.value = false;
    usageDaily.value = [];
    usageDailyStatus.value = "idle";
  }

  async function loadClients(): Promise<void> {
    if (clientsStatus.value === "loading") return;
    clientsStatus.value = "loading";
    clientsError.value = null;
    try {
      const page = await fetchBillingClients();
      clients.value = page.items ?? [];
      // Auto-pick a default client when none stored, prefer own.
      if (selectedClientId.value === null && clients.value.length > 0) {
        const own = clients.value.find((c) => c.relationship === "own") ?? clients.value[0];
        setSelectedClient(own.external_client_id);
      } else if (
        selectedClientId.value !== null &&
        !clients.value.some((c) => c.external_client_id === selectedClientId.value)
      ) {
        // Previously selected client no longer accessible (например, значение осталось от
        // прошлой сессии в этой же вкладке) — откатываемся на свой клиент, а не на первый в ответе API.
        const fallback = clients.value.find((c) => c.relationship === "own") ?? clients.value[0] ?? null;
        setSelectedClient(fallback ? fallback.external_client_id : null);
      }
      clientsStatus.value = "ready";
    } catch (error) {
      clientsStatus.value = "error";
      clientsError.value = extractErrorMessage(error);
    }
  }

  async function loadBalance(): Promise<void> {
    const id = selectedClientId.value;
    if (id === null) return;
    balanceStatus.value = "loading";
    balanceError.value = null;
    try {
      balance.value = await fetchBillingBalance(id);
      balanceStatus.value = "ready";
    } catch (error) {
      balanceStatus.value = "error";
      balanceError.value = extractErrorMessage(error);
    }
  }

  async function loadProfiles(options?: { status?: string }): Promise<void> {
    const id = selectedClientId.value;
    if (id === null) return;
    profilesStatus.value = "loading";
    profilesError.value = null;
    try {
      const page = await fetchBillingProfiles(id, options);
      profiles.value = page.items ?? [];
      profilesStatus.value = "ready";
    } catch (error) {
      profilesStatus.value = "error";
      profilesError.value = extractErrorMessage(error);
    }
  }

  async function loadProfileDetail(externalProfileId: number | string): Promise<BillingProfileDetail | null> {
    const id = selectedClientId.value;
    if (id === null) return null;
    profileDetailStatus.value = "loading";
    profileDetailError.value = null;
    try {
      profileDetail.value = await fetchBillingProfileDetail(id, externalProfileId);
      profileDetailStatus.value = "ready";
      return profileDetail.value;
    } catch (error) {
      profileDetailStatus.value = "error";
      profileDetailError.value = extractErrorMessage(error);
      return null;
    }
  }

  function applyDetailToSummary(detail: BillingProfileDetail): void {
    const idx = profiles.value.findIndex((p) => p.profile_id === detail.profile_id);
    const summary: BillingProfileSummary = {
      profile_id: detail.profile_id,
      display_name: detail.display_name,
      external_status: detail.external_status,
      email: detail.email,
      account: detail.account,
      allocation: detail.allocation,
    };
    if (idx === -1) profiles.value.push(summary);
    else profiles.value.splice(idx, 1, summary);
  }

  async function saveProfileAccount(
    externalProfileId: number | string,
    payload: BillingAccountUpdatePayload,
  ): Promise<BillingProfileDetail | null> {
    const id = selectedClientId.value;
    if (id === null) return null;
    const updated = await updateBillingProfileAccount(id, externalProfileId, payload);
    profileDetail.value = updated;
    applyDetailToSummary(updated);
    return updated;
  }

  async function disableProfileAccount(externalProfileId: number | string): Promise<void> {
    const id = selectedClientId.value;
    if (id === null) return;
    await disableBillingProfileAccount(id, externalProfileId);
    // Optimistic local sync — reflect disabled status.
    const idx = profiles.value.findIndex((p) => p.profile_id === Number(externalProfileId));
    if (idx !== -1) {
      const before = profiles.value[idx];
      profiles.value.splice(idx, 1, {
        ...before,
        account: before.account ? { ...before.account, status: "disabled" } : { status: "disabled", roles: [] },
      });
    }
    if (profileDetail.value && profileDetail.value.profile_id === Number(externalProfileId)) {
      profileDetail.value = {
        ...profileDetail.value,
        account: profileDetail.value.account
          ? { ...profileDetail.value.account, status: "disabled" }
          : { status: "disabled", roles: [] },
      };
    }
  }

  function setHistoryFilters(filters: Omit<BillingHistoryFilters, "client_id" | "cursor" | "limit">): void {
    historyFilters.value = { ...filters };
  }

  async function loadHistory(options?: { append?: boolean }): Promise<void> {
    const id = selectedClientId.value;
    if (id === null) return;
    historyStatus.value = "loading";
    historyError.value = null;
    try {
      const filters: BillingHistoryFilters = {
        client_id: id,
        limit: HISTORY_PAGE_SIZE,
        ...historyFilters.value,
      };
      if (options?.append && historyCursor.value) {
        filters.cursor = historyCursor.value;
      }
      const page = await fetchBillingHistory(filters);
      const incoming = page.items ?? [];
      history.value = options?.append ? [...history.value, ...incoming] : incoming;
      historyCursor.value = page.next_cursor ?? null;
      historyHasMore.value = !!page.next_cursor && incoming.length > 0;
      historyStatus.value = "ready";
    } catch (error) {
      historyStatus.value = "error";
      historyError.value = extractErrorMessage(error);
    }
  }

  /**
   * Расход по дням за последние USAGE_WINDOW_DAYS дней включая сегодня. Ряд
   * приходит плотным и уже посчитанным по ledger, поэтому не зависит от числа
   * операций клиента. Ошибка некритична: спарклайн просто не показывается.
   */
  async function loadUsageDaily(): Promise<void> {
    const id = selectedClientId.value;
    if (id === null) return;
    usageDailyStatus.value = "loading";
    try {
      const page = await fetchBillingUsageDaily({ client_id: id, ...usageWindow(USAGE_WINDOW_DAYS, new Date()) });
      usageDaily.value = page.items ?? [];
      usageDailyStatus.value = "ready";
    } catch (error) {
      console.error("billing: loadUsageDaily", error);
      usageDaily.value = [];
      usageDailyStatus.value = "error";
    }
  }

  async function loadTariffs(): Promise<void> {
    if (tariffsStatus.value === "loading") return;
    tariffsStatus.value = "loading";
    tariffsError.value = null;
    try {
      const page = await fetchBillingTariffs();
      tariffs.value = page.items ?? [];
      tariffsStatus.value = "ready";
    } catch (error) {
      tariffsStatus.value = "error";
      tariffsError.value = extractErrorMessage(error);
    }
  }

  async function loadPricingRules(): Promise<void> {
    if (pricingRulesStatus.value === "loading") return;
    pricingRulesStatus.value = "loading";
    pricingRulesError.value = null;
    try {
      const page = await fetchBillingPricingRules();
      pricingRules.value = page.items ?? [];
      pricingRulesStatus.value = "ready";
    } catch (error) {
      pricingRulesStatus.value = "error";
      pricingRulesError.value = extractErrorMessage(error);
    }
  }

  function versionId(version: TariffVersion): string | undefined {
    return version.id ?? version.tariff_version_id ?? version.version_id;
  }

  /**
   * Загружает прайсинг по длительности для тарифа с его АКТИВНОЙ версии
   * (`metadata.price_by_months`) — в каталоге его нет. Выбираем версию по
   * `active_tariff_version_id` из каталога, иначе — первую версию с прайсингом.
   * Результат кэшируем по коду тарифа и возвращаем.
   */
  async function loadTariffPricing(code: string): Promise<BillingPriceByMonths> {
    const activeVersionId = tariffs.value.find((t) => t.code === code)?.active_tariff_version_id;
    const page = await fetchBillingTariffVersions(code);
    const versions = page.items ?? [];
    const active = activeVersionId ? versions.find((v) => versionId(v) === activeVersionId) : undefined;
    const selected = active ?? versions.find((v) => v.metadata?.price_by_months);
    const pricing = selected?.metadata?.price_by_months ?? {};
    tariffPricing.value = { ...tariffPricing.value, [code]: pricing };
    return pricing;
  }

  async function loadTokenPrice(): Promise<void> {
    try {
      tokenPrice.value = await fetchTokenPrice();
    } catch (error) {
      // некритично: блок докупки покажет, что цена посчитается на оплате
      console.error("billing: loadTokenPrice", error);
      tokenPrice.value = null;
    }
  }

  // Возвращает ссылку на оплату; редирект (в текущей вкладке) делает UI.
  async function buyTariff(payload: BillingTariffPayPayload): Promise<string | null> {
    purchaseStatus.value = "loading";
    purchaseError.value = null;
    try {
      const { link } = await payTariff(payload);
      purchaseStatus.value = "ready";
      return link ?? null;
    } catch (error) {
      purchaseStatus.value = "error";
      purchaseError.value = mapPurchaseError(error);
      throw error;
    }
  }

  /**
   * Возврат на бесплатный тариф: платежа нет, поэтому и ссылки нет — бэк
   * применяет тариф сам (CAB-602 п.1). Текст отказа отдаём вызывающему как есть:
   * бэк формулирует его понятнее общего фоллбека («пакет в очереди…»).
   */
  async function switchToFreeTariff(): Promise<void> {
    purchaseStatus.value = "loading";
    purchaseError.value = null;
    try {
      await applyFreeTariff();
      purchaseStatus.value = "ready";
    } catch (error) {
      purchaseStatus.value = "error";
      purchaseError.value = mapPurchaseError(error);
      throw error;
    }
  }

  async function buyTokens(tokens: number, agreementAccepted: boolean): Promise<string | null> {
    purchaseStatus.value = "loading";
    try {
      const { link } = await accrueTokens(tokens, agreementAccepted);
      purchaseStatus.value = "ready";
      return link ?? null;
    } catch (error) {
      purchaseStatus.value = "error";
      throw error;
    }
  }

  function reset(): void {
    clients.value = [];
    selectedClientId.value = null;
    balance.value = null;
    profiles.value = [];
    profileDetail.value = null;
    history.value = [];
    historyCursor.value = null;
    historyHasMore.value = false;
    usageDaily.value = [];
    tariffs.value = [];
    pricingRules.value = [];
    tariffPricing.value = {};
    tokenPrice.value = null;
    clientsStatus.value = "idle";
    balanceStatus.value = "idle";
    profilesStatus.value = "idle";
    profileDetailStatus.value = "idle";
    historyStatus.value = "idle";
    usageDailyStatus.value = "idle";
    tariffsStatus.value = "idle";
    pricingRulesStatus.value = "idle";
    purchaseStatus.value = "idle";
    purchaseError.value = null;
  }

  return {
    clients,
    clientsStatus,
    clientsError,
    selectedClientId,
    selectedClient,
    balance,
    balanceStatus,
    balanceError,
    profiles,
    profilesStatus,
    profilesError,
    profileDetail,
    profileDetailStatus,
    profileDetailError,
    history,
    historyStatus,
    historyError,
    historyHasMore,
    historyFilters,
    usageDaily,
    usageDailyStatus,
    tariffs,
    tariffsStatus,
    tariffsError,
    pricingRules,
    pricingRulesStatus,
    pricingRulesError,
    tariffPricing,
    tokenPrice,
    purchaseStatus,
    purchaseError,
    currentTariffCode,
    myRoles,
    isAdminLikeRole,
    setSelectedClient,
    loadClients,
    loadBalance,
    loadProfiles,
    loadProfileDetail,
    saveProfileAccount,
    disableProfileAccount,
    setHistoryFilters,
    loadHistory,
    loadUsageDaily,
    loadTariffs,
    loadPricingRules,
    loadTariffPricing,
    loadTokenPrice,
    buyTariff,
    switchToFreeTariff,
    buyTokens,
    reset,
  };
});
